import { useCallback, useEffect, useState } from 'react';
import { AttributeModel, AttributeValue } from '../models/attribute.model';
import { UpdateAttributeViewWithoutLocation } from '../types/attribute.type';
import { EquipmentCharacteristicsModel } from '../models/equipment-characteristics.model';

const ABYLA_ROOT_TYPE_ATTRIBUTE_CODE: string = '-46';
const ABYLA_FULL_PATH_ATTRIBUTE_CODE: string = '-42';
const ABYLA_CHARACTERISTIC_FIELD_ATTRIBUTE_CODE: string = '58';
const ABYLA_NAME_ATTRIBUTE_CODE: string = '-1';
const ABYLA_EQUIPMENT_TYPE_CODE: string = '7';

export const useAttributes = (
  getAttributs: (assetId: string) => Promise<AttributeModel[]>,
  updateAttribute: (
    componentId: string,
    attributeId: string,
    updateAttribute: UpdateAttributeViewWithoutLocation
  ) => Promise<Response>,
  getCharacteristics: (componentTypeCodes: string[]) => Promise<EquipmentCharacteristicsModel[]>
) => {
  const [attributes, setAttributes] = useState<AttributeModel[]>([]);
  const [isAttributesLoading, setIsAttributesLoading] = useState(true);
  const [allCharacteristics, setAllCharacteristics] = useState<EquipmentCharacteristicsModel[]>([]);
  const [equipmentIdSelected, selectEquipmentId] = useState('');

  const [attributeName, setAttributeName] = useState('');

  const getEquipmentCodes = useCallback((attributes: AttributeModel[]) => {
    return Array.from(new Set(
      getAttributeNamesIfEquipment(attributes).map(attribute => attribute.substring(0, 4))
    ));
  }, []);

  const setAttributesAndCharacteristics = useCallback(async () => {
    const attributesModel = await getAttributs(equipmentIdSelected);
    setAttributes(attributesModel);
    const equipmentCodes = getEquipmentCodes(attributesModel);
    let characteristics = await getCharacteristics(equipmentCodes);
    setAllCharacteristics(characteristics);
  }, [equipmentIdSelected, getAttributs, getCharacteristics, getEquipmentCodes]);

  useEffect(() => {
    if (equipmentIdSelected) {
      setIsAttributesLoading(true);
      setAttributesAndCharacteristics().then(() => setIsAttributesLoading(false));
    }
  }, [equipmentIdSelected, setAttributesAndCharacteristics]);

  const attributesForEquipmentIdSelected = attributes.filter(({ componentId }) => componentId === equipmentIdSelected);

  const getEquipmentCharacteristicsCurrentValues = (attributes: AttributeModel[]) => Array.from(new Set(
    getAttributeNamesIfEquipment(attributes).map(attribute => attribute.substring(4))
  ));

  const getAttributeNamesIfEquipment = (attributes: AttributeModel[]) => {
    let equipmentComponentIds = attributes.filter(attribute =>
      attribute.id === ABYLA_ROOT_TYPE_ATTRIBUTE_CODE &&
      attribute.value === ABYLA_EQUIPMENT_TYPE_CODE)
      .map(it => it.componentId);
    return equipmentComponentIds ? attributes
      .filter(attribute => equipmentComponentIds.includes(attribute.componentId))
      .filter(attribute => attribute.id === ABYLA_NAME_ATTRIBUTE_CODE)
      .map(attribute => attribute.value.toString()) : [];
  };

  const equipmentAttributeNameSelected = getAttributeNamesIfEquipment(attributesForEquipmentIdSelected)[0] || null;

  const equipmentCodeForEquipmentSelected = getEquipmentCodes(attributesForEquipmentIdSelected)[0] || null;

  const selectedEquipmentCharacteristicsInitialValues = getEquipmentCharacteristicsCurrentValues(attributesForEquipmentIdSelected)[0] || null;

  const getEquipmentCharacteristics = (equipmentCode: string) => {
    const characteristicAttribute = attributesForEquipmentIdSelected
      .find(attribute => attribute.id === ABYLA_CHARACTERISTIC_FIELD_ATTRIBUTE_CODE);

    return allCharacteristics
      .filter((componentType) => componentType.code === equipmentCode.substring(0, 4))
      .map(characteristic => {
        return {
          ...characteristic,
          isEditable: characteristicAttribute?.isEditable,
        };
      });
  };

  const selectedEquipmentCharacteristics = (equipmentCodeForEquipmentSelected != null) ?
    getEquipmentCharacteristics(equipmentCodeForEquipmentSelected)[0] : null;


  const onAttributeValueChanged = useCallback(
    (componentId: string, attributeId: string, newValue: any) =>
      updateAttribute(componentId, attributeId, { value: newValue }).then(() => {
        setAttributes((attributeList) =>
          attributeList.map((attribute) => {
            if (attribute.id === attributeId && attribute.componentId === componentId) {
              attribute.value = newValue;
            }
            return attribute;
          })
        );
      }),
    [updateAttribute]
  );

  const getUpdatedSelectedEquipmentAttributeName = (attributeName: string | undefined, characteristicType: 'LINE' | 'COLUMN', characteristicValue: string) => {
    if (!attributeName) return undefined;
    if (characteristicType === 'LINE') {
      return attributeName.replace(/[0-9]([0-9])$/, characteristicValue + '$1');
    }
    return attributeName.replace(/[0-9]$/, characteristicValue);
  };

  function getUpdatedCharacteristicsField(currentAttributeValue: AttributeValue, selectedLabel: string, selectedCharacteristicType: 'LINE' | 'COLUMN') {
    if (selectedCharacteristicType === 'LINE') {
      let currentColumnPart = `${ currentAttributeValue }`.split('-')[1];
      return `${ selectedLabel }-${ currentColumnPart }` || '';
    }
    let currentLinePart = `${ currentAttributeValue }`.split('-')[0];
    return `${ currentLinePart }-${ selectedLabel }` || '';
  }

  const onCharacteristicChanged = useCallback(
    (componentId: string, equipmentAttributeName: string | null, newCharacteristicValue: string, label: string, characteristicType: 'LINE' | 'COLUMN') => {

      const name = getUpdatedSelectedEquipmentAttributeName(
        equipmentAttributeName || undefined,
        characteristicType,
        newCharacteristicValue
      );

      if (name) {
        updateAttribute(componentId, ABYLA_NAME_ATTRIBUTE_CODE, { value: name })
          .then(() => {
            setAttributes((attributeList) =>
              attributeList.map((attribute) => {
                if (attribute.id === ABYLA_NAME_ATTRIBUTE_CODE && attribute.componentId === componentId) {
                  attribute.value = name!!;
                }
                if (attribute.id === ABYLA_FULL_PATH_ATTRIBUTE_CODE && attribute.componentId === componentId) {
                  attribute.value = `${ attribute.value }`.replace(/^([A-Z]{2}[0-9]{4})\//, name + '/');
                }
                if (attribute.id === ABYLA_CHARACTERISTIC_FIELD_ATTRIBUTE_CODE && attribute.componentId === componentId) {
                  attribute.value = getUpdatedCharacteristicsField(attribute.value, label, characteristicType);
                }
                return attribute;
              })
            );
          });
      }
    },
    [updateAttribute]
  );

  return {
    attributesForEquipmentIdSelected,
    isAttributesLoading,
    equipmentIdSelected,
    selectEquipmentId,
    attributeName,
    setAttributeName,
    onAttributeValueChanged,
    onCharacteristicChanged,
    selectedEquipmentCharacteristics,
    selectedEquipmentCharacteristicsInitialValues,
    equipmentAttributeNameSelected
  };
};
